<template>
  <div class="fp-page">
    <div class="fp-glow fp-glow--1" />
    <div class="fp-glow fp-glow--2" />

    <div class="fp-card">
      <div class="fp-brand">
        <img src="@/assets/logo.png" class="fp-brand__logo" />
      </div>

      <h1 class="fp-title">Recuperar contraseña</h1>
      <p class="fp-subtitle">Ingresa tu correo y te enviaremos un enlace para restablecer tu contraseña.</p>

      <form @submit.prevent="forgetPassword" class="fp-form">
        <div class="fp-field">
          <label class="fp-label">Correo electrónico</label>
          <input
            class="input"
            type="email"
            name="email"
            placeholder="tu@empresa.com"
            autofocus=""
            autocomplete="off"
            v-model="email"
          />
        </div>

        <div class="fp-error" v-if="error">{{ error }}</div>

        <button
          class="fp-btn"
          :class="{ 'is-loading': loading }"
          :disabled="isCheckEmailField || loading"
        >
          Enviar enlace
        </button>
      </form>

      <div class="fp-divider" />

      <router-link class="fp-back" :to="{ name: 'login' }">
        <i class="fas fa-arrow-left" /> Recordé mi contraseña
      </router-link>
    </div>

    <p class="fp-tagline">Smart Native</p>
  </div>
</template>

<script>
import { mapActions, mapState, mapMutations } from 'vuex'

export default {
  name: 'ForgotPassword',
  data () {
    return {
      email: '',
      isDisabled: true,
      loading: false,
      error: null
    }
  },
  computed: {
    ...mapState({ authentication: state => state.authentication }),
    isCheckEmailField () {
      return !this.email
    }
  },
  methods: {
    ...mapMutations(['setAuthenticationValue']),
    ...mapActions(['forgetPass']),
    forgetPassword () {
      this.loading = true
      this.error = null
      this.forgetPass({ email: this.email })
        .then(() => {
          this.loading = false
          this.$router.push('/login')
        })
        .catch(() => {
          this.loading = false
          this.error = 'No encontramos una cuenta con ese correo. Verifica e intenta de nuevo.'
        })
    }
  }
}
</script>

<style lang="scss" scoped>
@import '../styles/variables';

.fp-page {
  position: relative;
  min-height: 100vh;
  background: #0d1525;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  padding: 2rem;
}

.fp-glow {
  position: absolute;
  border-radius: 50%;
  pointer-events: none;

  &--1 {
    width: 700px;
    height: 700px;
    background: radial-gradient(circle, rgba(76, 112, 255, 0.22), transparent 65%);
    filter: blur(60px);
    top: -200px;
    right: -150px;
  }
  &--2 {
    width: 400px;
    height: 400px;
    background: radial-gradient(circle, rgba(76, 112, 255, 0.1), transparent 70%);
    filter: blur(80px);
    bottom: -100px;
    left: -80px;
  }
}

.fp-card {
  position: relative;
  z-index: 1;
  background: rgba(255, 255, 255, 0.04);
  backdrop-filter: blur(24px);
  -webkit-backdrop-filter: blur(24px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  padding: 2.5rem 2.25rem;
  width: 100%;
  max-width: 460px;
  box-shadow: 0 32px 80px rgba(0, 0, 0, 0.5);
}

.fp-brand {
  margin-bottom: 2rem;
  display: flex;
  justify-content: center;

  &__logo {
    width: 200px;
    opacity: 0.92;
  }
}

.fp-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #fff;
  margin-bottom: 0.3rem;
  text-align: center;
}

.fp-subtitle {
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.45);
  margin-bottom: 2rem;
  text-align: center;
  line-height: 1.6;
}

.fp-form {
  display: flex;
  flex-direction: column;
  gap: 1.1rem;
}

.fp-field {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;

  .input {
    height: 35px;
  }
}

.fp-label {
  font-size: 0.8rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.7);
}

.fp-error {
  font-size: 0.82rem;
  color: #ff3860;
  background: rgba(255, 56, 96, 0.1);
  border: 1px solid rgba(255, 56, 96, 0.25);
  border-radius: 8px;
  padding: 0.6rem 0.85rem;
}

.fp-btn {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 46px;
  background: $blue;
  color: #fff;
  border: none;
  border-radius: 10px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  margin-top: 0.5rem;
  transition: background 0.2s ease, transform 0.15s ease;

  &:hover:not(:disabled) {
    background: darken(#4c70ff, 8%);
    transform: translateY(-1px);
  }
  &:active { transform: translateY(0); }
  &:disabled { opacity: 0.5; cursor: not-allowed; }

  &.is-loading {
    color: transparent;
    pointer-events: none;

    &::after {
      content: '';
      position: absolute;
      width: 16px;
      height: 16px;
      border: 2px solid rgba(255, 255, 255, 0.4);
      border-top-color: #fff;
      border-radius: 50%;
      animation: fp-spin 0.6s linear infinite;
    }
  }
}

@keyframes fp-spin {
  to { transform: rotate(360deg); }
}

.fp-divider {
  height: 1px;
  background: rgba(255, 255, 255, 0.08);
  margin: 1.5rem 0 1.25rem;
}

.fp-back {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  font-size: 0.82rem;
  color: #7b9fff;
  text-decoration: none;

  &:hover { color: #fff; text-decoration: underline; }
}

.fp-tagline {
  margin-top: 1.25rem;
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.25);
  letter-spacing: 0.08em;
  text-transform: uppercase;
}
</style>
